import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import analyticsUtils from 'app/client/utils/analyticsUtils';
import ConnectedNetworkView from 'app/shared/components/ConnectedNetworkView';
import ModalActions from 'app/shared/actions/ModalActions';
import ModalConstants from 'app/shared/constants/ModalConstants';
import PropertyConstants from 'app/shared/constants/PropertyConstants';
import { AppSelectors, PropertiesSelectors } from 'app/shared/selectors';

import { StyledText } from 'app/shared/components/ConnectedListingFooter/StyledComponents';
import { StyledTextButton } from './StyledComponents';

const { track } = analyticsUtils;
const { GA_EVENTS } = AnalyticsConstants;
const { PAID_INCLUSION_VALUE_PROP_MODAL } = ModalConstants;
const { SYNDICATION_OVERVIEW_STATUS } = PropertyConstants;
const {
    makeIsScreenConstellationMobile,
    makeIsScreenConstellationLG,
} = AppSelectors;

// eslint-disable-next-line max-len
const listingDeclinedReasonsPage = 'https://help.zillowrentalmanager.com/hc/en-us/articles/217482388-Why-was-my-listing-Declined-';

const propTypes = {
    isInPaidMarket: PropTypes.bool.isRequired,
    seattleHousingConnectorStatus: PropTypes.string.isRequired,
    syndication: PropTypes.object.isRequired,
};

const LearnMore = ({ isInPaidMarket, seattleHousingConnectorStatus, syndication }) => {
    const dispatch = useDispatch();
    const {
        leadGuarantee: {
            isEligible: isEligibleForLeadGuarantee,
        },
    } = useSelector(state => PropertiesSelectors.makeCurrentProperty(state));
    const isScreenMobile = useSelector(state => makeIsScreenConstellationMobile(state));
    const isScreenLg = useSelector(state => makeIsScreenConstellationLG(state));
    const { overview: { status: syndicationOverviewStatus } } = syndication;

    const handlePaidInclusionLearnMoreClick = () => {
        dispatch(
            ModalActions.showModal({
                modalType: PAID_INCLUSION_VALUE_PROP_MODAL,
                onOpen: () => dispatch(track.GAEventV2(GA_EVENTS.VALUE_PROP_VIEW)),
                onClose: () => dispatch(track.GAEventV2(GA_EVENTS.VALUE_PROP_DISMISS)),
            })
        );
        return false;
    };

    const renderPublishText = () => {
        const viewKey = isEligibleForLeadGuarantee ? 'leadGuarantee' : 'nonLeadGuaranteePaidInclusion';
        let sizeKey = 'xl';

        if (isScreenMobile) {
            sizeKey = 'sm';
        } else if (isScreenLg) {
            sizeKey = 'lg';
        }

        if (syndicationOverviewStatus === SYNDICATION_OVERVIEW_STATUS.PUBLISHED) {
            return 'View on Zillow Rental Network';
        }
        if (syndicationOverviewStatus === SYNDICATION_OVERVIEW_STATUS.PUBLISHING) {
            return seattleHousingConnectorStatus === 'exclusive' ? ''
                : `Published to ${syndication.overview.publishedCount} of 3 sites...`;
        }

        if (syndicationOverviewStatus === SYNDICATION_OVERVIEW_STATUS.DECLINED) {
            return (
                <StyledTextButton as="a" href={listingDeclinedReasonsPage} target="_blank">
                    Learn why it was declined
                </StyledTextButton>
            );
        }

        if (syndicationOverviewStatus === SYNDICATION_OVERVIEW_STATUS.PARTIALLY_PUBLISHED) {
            return (
                <StyledTextButton
                    as="a"
                    href={listingDeclinedReasonsPage}
                    target="_blank"
                >
                        Learn why
                </StyledTextButton>
            );
        }

        if (syndicationOverviewStatus === SYNDICATION_OVERVIEW_STATUS.INACTIVE && !isInPaidMarket) {
            return <StyledText>Publish to the Zillow Rental Network</StyledText>;
        }

        const screeWidthTextMappings = {
            leadGuarantee: {
                sm: 'Incl. Lead Guarantee',
                lg: 'Includes Lead Guarantee',
                xl: 'Publish this listing with Lead Guarantee',
            },
            nonLeadGuaranteePaidInclusion: {
                sm: 'Learn more',
                lg: 'Learn about Zillow Rental Network',
                xl: 'Learn about Zillow Rental Network',
            },
        };
        return (
            <StyledTextButton onClick={handlePaidInclusionLearnMoreClick}>
                {screeWidthTextMappings[viewKey][sizeKey]}
            </StyledTextButton>
        );
    };

    return (
        <>
            {(
                syndicationOverviewStatus === SYNDICATION_OVERVIEW_STATUS.PUBLISHED ||
                syndicationOverviewStatus === SYNDICATION_OVERVIEW_STATUS.PUBLISHING) ? (
                    <ConnectedNetworkView syndication={syndication} label={renderPublishText()} />
                ) : (
                    <>
                        {renderPublishText()}
                    </>
                )}
        </>
    );
};

LearnMore.propTypes = propTypes;

export default LearnMore;
